import Bugsnag, { type Event } from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import type { App } from 'vue'

import type { CurrentUser } from '@/javascript/interfaces'

const bugsnagApiKey = import.meta.env.BUGSNAG_JS_API_KEY

// Hosts of third-party scripts loaded from external CDNs. Errors whose stack
// originates in one of these scripts are vendor-internal (e.g. the Appcues
// iframe DOM patcher reading contentDocument.body on a null contentDocument)
// and are not actionable in our codebase. Add a host here when a new hosted
// vendor script generates non-actionable noise.
export const THIRD_PARTY_SCRIPT_HOSTS = [
	'fast.appcues.com',
	'px.ads.linkedin.com',
	'snap.licdn.com',
	'www.google-analytics.com',
	'www.googletagmanager.com',
]

/**
 * Determines whether an error's stack trace originates in a known third-party
 * vendor script, in which case it is not actionable by us and should not be
 * reported to Bugsnag.
 * @param stacktrace - Stack frames from the reported error
 * @returns true when any frame belongs to a denylisted vendor host
 */
export const isThirdPartyScriptError = (
	stacktrace: Array<{ file?: string }>
): boolean =>
	stacktrace.some(frame =>
		THIRD_PARTY_SCRIPT_HOSTS.some(host => frame.file?.includes(host))
	)

/**
 * Determines whether a stack trace points at the HTML document rather than at
 * any JavaScript file. That happens for code running in an inline
 * <script> or injected at runtime by a tag manager: the browser reports the
 * page URL as the frame's file, and the line number depends on how long the
 * rendered HTML happens to be. Bugsnag has no filename or method to hash, so it
 * falls back to its "js-html" strategy and groups on that line number, which
 * makes one recurring crash fan out into a new error group per page and per
 * deploy.
 *
 * Frames with no file at all are allowed alongside the document frame: scripts
 * a browser injects itself (e.g. Chrome for Android's Translate) report no URL
 * for any frame, and Bugsnag fills in only the top one from the page URL.
 *
 * "InvalidError" is excluded even though it matches the same shape: it's
 * Bugsnag's own synthetic class for non-Error values thrown via
 * window.onerror/unhandledrejection, and the browser reports the page URL as
 * the frame's file for these regardless of where the value actually came from.
 * Bugsnag's default file+line grouping already separates these by page; this
 * predicate would otherwise collapse them into one sitewide bucket per
 * generic wrapper message.
 * @param stacktrace - Stack frames from the reported error
 * @param errorClass - The reported error's class name
 * @returns true when at least one frame has a file and none comes from a
 * JavaScript file
 */
export const isInlineDocumentError = (
	stacktrace: Array<{ file?: string }>,
	errorClass?: string
): boolean => {
	if (errorClass === 'InvalidError') {
		return false
	}

	const files = stacktrace.map(frame => (frame.file ?? '').split(/[?#]/)[0])

	return files.some(file => file.length > 0) && files.every(file => !file.endsWith('.js'))
}

/**
 * Determines whether an error message is the browser's ResizeObserver loop
 * notification. This fires when a ResizeObserver callback doesn't finish
 * within one frame; it is benign, not a crash, and carries no stack location
 * to investigate.
 * @param errorMessage - The reported error's message
 * @returns true when the message is a ResizeObserver loop notification
 */
export const isResizeObserverLoopError = (errorMessage: string): boolean =>
	errorMessage.startsWith('ResizeObserver loop')

/**
 * Determines whether a non-Error rejection/throw value is CefSharp's "Object Not
 * Found Matching Id" signature. CefSharp is the Chromium engine Microsoft Outlook
 * embeds to prescan links in emails ("Safe Links"); its bot crawler pokes at page
 * objects that don't match what it expects, producing this exact format. It's bot
 * traffic, not a real user, and carries no actionable information.
 * @param nonErrorPayload - The value from Bugsnag's "non-error parameter" metadata
 * @returns true when the payload is CefSharp's bot-crawler signature
 */
export const isCefSharpBotError = (nonErrorPayload: unknown): boolean =>
	typeof nonErrorPayload === 'string' &&
	nonErrorPayload.startsWith('Object Not Found Matching Id')

/**
 * Determines whether a non-Error rejection/throw value carries no information at
 * all (a plain object with no own keys). Wistia's player throws exactly this when
 * the underlying <video> element fails to decode; VideoPlayer.tsx surfaces that
 * failure to the user directly via a capturing HTMLMediaElement "error" listener,
 * so the Bugsnag report adds nothing actionable.
 * @param nonErrorPayload - The value from Bugsnag's "non-error parameter" metadata
 * @returns true when the payload is a plain object with no own keys
 */
export const isEmptyNonErrorPayload = (nonErrorPayload: unknown): boolean =>
	typeof nonErrorPayload === 'object' &&
	nonErrorPayload !== null &&
	Object.keys(nonErrorPayload).length === 0

// Hosts whose network requests generate high-volume, low-value breadcrumbs (Wistia
// video segment delivery, New Relic's own beacon calls). These bury the requests
// that actually help diagnose a crash under dozens of near-identical entries. Add
// a host here when a new noisy vendor request starts dominating breadcrumb trails.
export const NOISY_BREADCRUMB_REQUEST_HOSTS = [
	'bam.nr-data.net',
	'distillery.wistia.com',
	'embed-cloudfront.wistia.com',
	'pipedream.wistia.com',
]

/**
 * Determines whether a breadcrumb is a network request to a known noisy vendor
 * host, in which case it adds no diagnostic value and should not be recorded.
 * @param breadcrumb - The breadcrumb Bugsnag is about to record
 * @returns true when the breadcrumb is a request to a denylisted noisy host
 */
export const isNoisyRequestBreadcrumb = (
	breadcrumb: { metadata?: { url?: string }; type?: string }
): boolean =>
	breadcrumb.type === 'request' &&
	NOISY_BREADCRUMB_REQUEST_HOSTS.some(host => breadcrumb.metadata?.url?.includes(host))

/**
 * Determines whether an error message is the browser's own boilerplate for a
 * blocked eval()/Function() call under our Content-Security-Policy. The call
 * site is always reported as a blob: URL under our own origin regardless of
 * which script created it, so it can never be attributed to a vendor host the
 * way THIRD_PARTY_SCRIPT_HOSTS does — in the confirmed case, Appcues evaluating
 * fetched flow content, which our CSP's script-src correctly does not allow
 * (see config/initializers/content_security_policy.rb; Heap's eval-dependent
 * Visual Labeler is declined for the same reason). The message also embeds a
 * per-request nonce, so it can never group across page loads either way.
 *
 * The violation is independently captured server-side via the CSP report-uri
 * (see CspViolationReportsController), so suppressing this client-side copy
 * does not reduce security visibility.
 * @param errorMessage - The reported error's message
 * @returns true when the message is a CSP eval-violation notice
 */
export const isCspEvalViolationError = (errorMessage: string): boolean =>
	errorMessage.includes('Evaluating a string as JavaScript violates the following Content Security Policy directive')

/**
 * Determines whether an error message comes from a script Brave for iOS injects
 * into the page. Brave for iOS is built on Firefox for iOS and, like it,
 * namespaces its injected scripts (reader mode, YouTube quality, etc.) under
 * window.__firefox__, which our code never defines.
 * @param errorMessage - The reported error's message
 * @returns true when the message references the injected-script namespace
 */
export const isBraveIosInjectedScriptError = (errorMessage: string): boolean =>
	errorMessage.includes('__firefox__')

const getCurrentUser = (): Partial<CurrentUser> | undefined => {
	if (typeof window === 'undefined') {
		return undefined
	}

	return window.current_user
}

// Set by createVueApp.ts once its own unhandledrejection listener is attached.
// window.Event#preventDefault() can't stop Bugsnag's own unhandledrejection listener
// (registered here at module load, before createVueApp() ever runs) from reporting
// first, so this flag is the only reliable way for onError to know that a page's Vue
// listener will also send a normalized substitute report and the native one is a
// duplicate. Pages with no Vue app (and no substitute report) never set it, so their
// native reporting is unaffected.
let hasVueUnhandledRejectionListener = false

/**
 * Marks that createVueApp.ts's unhandledrejection listener is active on this page, so
 * onError can drop the duplicate native InvalidError report for the same rejection.
 */
export const markVueUnhandledRejectionListenerActive = (): void => {
	hasVueUnhandledRejectionListener = true
}

/**
 * Determines whether an InvalidError report duplicates the normalized Error that
 * createVueApp.ts's own unhandledrejection listener already sends for the same
 * rejection (see markVueUnhandledRejectionListenerActive above for why this can't be
 * suppressed via the DOM event itself).
 * @param errorClass - The reported error's class name
 * @param hasUnhandledRejectionMetadata - Whether the event carries Bugsnag's own
 * "unhandledrejection handler" metadata tab
 * @param vueListenerActive - Whether createVueApp.ts's unhandledrejection listener has
 * been attached on this page
 * @returns true when the report is a duplicate that should be suppressed
 */
export const isDuplicateVueRejectionReport = (
	errorClass: string | undefined,
	hasUnhandledRejectionMetadata: boolean,
	vueListenerActive: boolean
): boolean =>
	errorClass === 'InvalidError' && vueListenerActive && hasUnhandledRejectionMetadata

/**
 * Bugsnag's onError callback: applies noise filters and drops the duplicate Vue
 * rejection report (see isDuplicateVueRejectionReport above).
 * @param event - The event Bugsnag is about to report
 * @returns false to suppress the report, true to send it
 */
export const handleBugsnagError = (event: Event): boolean => {
	const errorMessage = event.errors[0]?.errorMessage ?? ''
	const stacktrace = event.errors[0]?.stacktrace ?? []
	const errorClass = event.errors[0]?.errorClass
	const nonErrorPayload: unknown =
		event.getMetadata('window onerror', 'non-error parameter') ??
		event.getMetadata('unhandledrejection handler', 'non-error parameter')

	// "InboundFilters" is a Zendesk-specific identifier that does not exist anywhere
	// in our codebase, so this message is always a Zendesk widget error.
	// Remove this filter once the Zendesk widget is removed.
	if (errorMessage.includes('InboundFilters')) {
		return false
	}

	// Stack frames with file === 'unknown' indicate scripts that are not served by our
	// infrastructure and cannot be traced back to our code (e.g. browser extensions,
	// injected third-party scripts). Suppress these as they are not actionable.
	if (stacktrace.some(frame => frame.file === 'unknown')) {
		return false
	}

	if (isResizeObserverLoopError(errorMessage)) {
		return false
	}

	if (isCspEvalViolationError(errorMessage)) {
		return false
	}

	if (isBraveIosInjectedScriptError(errorMessage)) {
		return false
	}

	if (isCefSharpBotError(nonErrorPayload)) {
		return false
	}

	// See VideoPlayer.tsx's HTMLMediaElement "error" listener for the user-facing
	// side of this failure.
	if (
		errorClass === 'InvalidError' &&
		event.context?.includes('video_library') &&
		isEmptyNonErrorPayload(nonErrorPayload)
	) {
		return false
	}

	if (
		isDuplicateVueRejectionReport(
			errorClass,
			event.getMetadata('unhandledrejection handler') !== undefined,
			hasVueUnhandledRejectionListener
		)
	) {
		return false
	}

	// Errors originating from Chrome extension scripts are not part of our codebase
	// and are not actionable.
	if (stacktrace.some(frame => frame.file?.startsWith('chrome-extension://'))) {
		return false
	}

	// Errors originating from hosted third-party vendor scripts (e.g. Appcues,
	// analytics/ads pixels) are vendor-internal and not actionable by us.
	if (isThirdPartyScriptError(stacktrace)) {
		return false
	}

	// Errors raised from inline or injected scripts carry no filename, so
	// Bugsnag groups them on the HTML line number and one crash becomes a new
	// error group on every deploy. Group them on class and message instead,
	// and keep the frame location in metadata for identifying the script.
	if (isInlineDocumentError(stacktrace, errorClass)) {
		const locationFrame = stacktrace.find(frame => frame.file)

		event.groupingHash =
			`inline-document:${errorClass ?? 'Error'}:${errorMessage}`
		event.addMetadata('inlineScript', {
			column: locationFrame?.columnNumber,
			file: locationFrame?.file,
			line: locationFrame?.lineNumber,
		})
	}

	return true
}

if (bugsnagApiKey && typeof window !== 'undefined') {
	const currentUser = getCurrentUser()

	Bugsnag.start({
		apiKey: bugsnagApiKey,
		appVersion: import.meta.env.APP_VERSION || 'unknown',
		onBreadcrumb(breadcrumb) {
			return !isNoisyRequestBreadcrumb(breadcrumb)
		},
		onError: handleBugsnagError,
		plugins: [new BugsnagPluginVue()],
		user: {
			email: currentUser?.email,
			id: currentUser?.id?.toString(),
			name: currentUser?.name,
		},
	})
}

/**
 * Attaches Bugsnag to a Vue 3 application
 * @param app - Vue 3 application instance
 */
export default function (app: App) {
	if (!bugsnagApiKey || typeof window === 'undefined') {
		if (import.meta.env.DEV) {
			return
		}

		return
	}

	try {
		const bugsnagVue = Bugsnag.getPlugin('vue')

		if (bugsnagVue && app) {
			app.use(bugsnagVue)
		}
	} catch (error) {
		if (import.meta.env.DEV) {
			console.error('Failed to initialize Bugsnag Vue plugin:', error)
		}
	}
}
